import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useUserSearchContext,
  UserSearchProvider,
} from "Providers/UserSearchProvider";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useSurveyContext as useSurveyFormContext } from "../Data/SurveyFormContext";
import { PulseBuilderStyledSearchDropdown } from "new_components/organisms/SearchDropdown";

// Question Types Configuration - matching existing system
const QUESTION_TYPES = [
  {
    value: "text",
    label: "Open Text",
    icon: "📝",
    description: "Open-ended text response",
  },
  {
    value: "scale",
    label: "Sliding Scale",
    icon: "📊",
    description: "Rating scale question",
  },
  {
    value: "multi",
    label: "Multiple Answer",
    icon: "☑️",
    description: "Select multiple options",
  },
  {
    value: "single",
    label: "Multiple Choice",
    icon: "⚪",
    description: "Select one option",
  },
];

// Icons
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
);

const PulseBuilderV2Integrated = ({ onSwitchToAI }) => {
  // Helper: read a reliable boolean from user_tasks -> survey_is_anonymous
  const getAnonymousFromFormData = (fd) => {
    const v = fd?.user_tasks?.find(
      (task) => task && task.task_type === "survey_is_anonymous"
    )?.value;
    if (typeof v === "string") return v === "true"; // normalize string values
    return !!v; // coerce to boolean
  };
  // Get contexts
  const surveyFormContext = useSurveyFormContext();
  const currentUser = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle back navigation with safe fallbacks
  const handleBackClick = () => {
    const returnTo = location?.state?.returnTo;
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }

    const historyIdx = typeof window !== 'undefined' && window.history && window.history.state && typeof window.history.state.idx === 'number'
      ? window.history.state.idx
      : 0;
    if (historyIdx > 0) {
      navigate(-1);
      return;
    }

    const ref = typeof document !== 'undefined' ? document.referrer || '' : '';
    if (ref.includes('/leader/audit/schedule-v2/pulse')) {
      navigate('/leader/audit/schedule-v2/pulse');
      return;
    }
    if (ref.includes('/leader/audit/schedule-v2')) {
      navigate('/leader/audit/schedule-v2');
      return;
    }

    // Default fallback to Schedule hub
    navigate('/leader/audit/schedule-v2');
  };

  const {
    formData,
    updateFormData,
    saveDraft,
    loading,
    getUserRoleForSurvey,
  } = surveyFormContext || {};

  // Identify whether we're editing an existing survey or creating a new one
  const isEditMode = useMemo(() => {
    // consider presence of an id (or backend-provided keys) as edit mode
    return Boolean(
      formData && (formData.id || formData.survey_id || formData.uuid)
    );
  }, [formData]);

  // Get current user's role for this survey
  const currentUserId = currentUser?.employee_id;
  const userRole = useMemo(() => {
    if (!formData || !currentUserId) return "creator"; // Default for new surveys
    return getUserRoleForSurvey
      ? getUserRoleForSurvey(formData, currentUserId) || "creator"
      : "creator";
  }, [formData, currentUserId, getUserRoleForSurvey]);

  // Local state for UI management
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [draggedQuestionId, setDraggedQuestionId] = useState(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Settings modal state - show by default for new surveys
  const [showSettingsModal, setShowSettingsModal] = useState(!isEditMode);
  const [surveySettings, setSurveySettings] = useState({
    title: formData?.name || "",
    description: formData?.user_tasks?.find(
        (task) => task?.task_type === "description"
      )?.text || formData?.description || "",
    anonymous: getAnonymousFromFormData(formData),
  });

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [shouldNavigateToScheduleAfterSave, setShouldNavigateToScheduleAfterSave] = useState(false);
  const { userList } = useUserSearchContext();

  // User permissions based on context
  const canEdit = userRole === "creator" || userRole === "collaborator";


  // Initialize local state with form data
  useEffect(() => {
    if (formData) {
      setSurveySettings({
        title: formData.name || "",
        description: formData?.user_tasks?.find(
        (task) => task?.task_type === "description"
      )?.text || formData?.description || "",
        anonymous: getAnonymousFromFormData(formData),
      });

      // Initialize shared users from user_tasks
      const collaborators =
        formData.user_tasks?.filter(
          (task) => task && task.task_type === "collaborator"
        ) || [];
      const viewers =
        formData.user_tasks?.filter((task) => task && task.task_type === "viewer") ||
        [];
      const allSharedUsers = [...collaborators, ...viewers].map((task) => ({
        id: task.user_id,
        name: task.name,
        email: task.email,
        role: task.task_type,
      }));

      if (allSharedUsers.length > 0) {
        setSharedUsers(allSharedUsers);
      }
    }
  }, [formData]);

  // Set document title based on edit mode
  useEffect(() => {
    const base = "Pulse Builder";
    if (isEditMode) {
      document.title = `${base} — Edit`;
    } else {
      document.title = `${base} — New`;
    }
  }, [isEditMode]);

  // Effect to handle navigation to schedule page after save
  useEffect(() => {
    if (shouldNavigateToScheduleAfterSave && formData?.id) {
      // Reset the flag
      setShouldNavigateToScheduleAfterSave(false);
      
      // Navigate to schedule page with proper returnTo
      const returnTo = `/leader/audit/schedule-v2/pulse/edit/${formData.id}`;
      
      navigate("/leader/audit/schedule-v2/pulse/schedule", {
        state: {
          existingDraftData: {
            ...formData,
            emailSubject: `Survey: ${formData.name || ""}`,
            emailBody: `Hi,\\n\\nYou have been invited to participate in the survey "${
              formData.name || ""
            }". Please follow the link to complete the survey.\\n\\nThank you!`,
            assignedUsersList: [],
          },
          returnTo,
        },
      });
    }
  }, [shouldNavigateToScheduleAfterSave, formData?.id, formData, navigate]);

  // Convert PulseBuilderV2 format to existing system format
  const convertToExistingFormat = (question) => ({
    id: question.id,
    question: question.question,
    aiEnhanced: question.aiEnhanced,
    task_type: "question",
    type: question.type, // Keep as string to match existing system
    scale: question.scale,
    qualifier: question.qualifier,
    options: question.options,
  });

  // Convert existing format to PulseBuilderV2 format
  const convertFromExistingFormat = (question) => ({
    id: question.id,
    question: question.question,
    type:
      typeof question.type === "object" ? question.type.value : question.type,
    aiEnhanced: question.aiEnhanced !== undefined ? question.aiEnhanced : true,
    task_type: "question",
    scale: question.scale,
    qualifier: question.qualifier,
    options: question.options,
  });

  // Get questions from formData, converted to PulseBuilderV2 format - memoized for performance
  const questions = useMemo(
    () => formData?.questions?.map(convertFromExistingFormat) || [],
    [formData?.questions]
  );
  const selectedQuestion = questions.find((q) => q.id === selectedQuestionId);

  // Question validation function to identify issues
  const getQuestionValidationIssues = useCallback((question) => {
    const issues = [];
    
    // Check if question text is empty
    if (!question.question?.trim()) {
      issues.push('Question text is required');
    }
    
    // Check if multiple choice questions have valid options
    if ((question.type === 'multi' || question.type === 'single') && question.options) {
      const validOptions = question.options.filter(opt => opt?.trim());
      if (validOptions.length === 0) {
        issues.push('At least one option is required');
      }
    }
    
    return issues;
  }, []);

  // Check if a question has validation issues
  const hasQuestionValidationIssues = useCallback((question) => {
    return getQuestionValidationIssues(question).length > 0;
  }, [getQuestionValidationIssues]);

  // Check if basic info (title and description) has validation issues
  const hasBasicInfoValidationIssues = useCallback(() => {
    const hasTitle = Boolean(formData?.name?.trim());
    // Get description from user_tasks array (where it's actually stored)
    const descriptionFromUserTasks = formData?.user_tasks?.find(
      (task) => task && task.task_type === "description"
    )?.text || "";
    const hasDescription = Boolean(formData?.description?.trim() || descriptionFromUserTasks.trim());
    return !hasTitle || !hasDescription;
  }, [formData?.name, formData?.description, formData?.user_tasks]);

  // Validate all survey data and show errors if any
  const validateSurveyAndShowErrors = useCallback(() => {
    const hasQuestionErrors = questions.some(question => hasQuestionValidationIssues(question));
    const hasBasicInfoErrors = hasBasicInfoValidationIssues();
    const hasErrors = hasQuestionErrors || hasBasicInfoErrors;
    setShowValidationErrors(hasErrors);
    return !hasErrors; // Return true if no errors
  }, [questions, hasQuestionValidationIssues, hasBasicInfoValidationIssues]);

  // Clear validation errors when questions are fixed
  useEffect(() => {
    if (showValidationErrors) {
      const hasQuestionErrors = questions.some(question => hasQuestionValidationIssues(question));
      const hasBasicInfoErrors = hasBasicInfoValidationIssues();
      const hasErrors = hasQuestionErrors || hasBasicInfoErrors;
      if (!hasErrors) {
        setShowValidationErrors(false);
      }
    }
  }, [questions, showValidationErrors, hasQuestionValidationIssues, hasBasicInfoValidationIssues, formData?.name, formData?.description]);

  // Survey setup progress tracking (moved below questions to avoid TDZ)
  const surveyProgress = useMemo(() => {
    const hasTitle = Boolean(formData?.name?.trim());
    // Get description from user_tasks array (where it's actually stored)
    const descriptionFromUserTasks = formData?.user_tasks?.find(
      (task) => task && task.task_type === "description"
    )?.text || "";
    const hasDescription = Boolean(formData?.description?.trim() || descriptionFromUserTasks.trim());
    const hasQuestions = questions.length > 0;
    
    return {
      basicInfo: hasTitle && hasDescription,
      questions: hasQuestions,
      steps: [
        { key: 'basicInfo', label: 'Basic Info', completed: hasTitle && hasDescription },
        { key: 'questions', label: 'Questions', completed: hasQuestions },
      ]
    };
  }, [formData?.name, formData?.description, formData?.user_tasks, questions.length]);

  // Add a new question
  const handleAddQuestion = useCallback(
    (type) => {
      const newQuestion = {
        id: uuidv4(),
        question: "",
        type: type,
        aiEnhanced: true,
        task_type: "question",
        scale: type === "scale" ? "0-10" : null,
        qualifier:
          type === "scale"
            ? ["Strongly Disagree", "Neutral", "Strongly Agree"]
            : null,
        options: type === "multi" || type === "single" ? [""] : null,
      };

      const updatedQuestions = [...questions, newQuestion];
      updateFormData({
        questions: updatedQuestions.map(convertToExistingFormat),
      });
      setSelectedQuestionId(newQuestion.id);
      setShowAddDropdown(false);
    },
    [questions, updateFormData]
  );

  // Update a question
  const handleQuestionUpdate = useCallback(
    (field, value) => {
      if (!selectedQuestionId) return;

      const updatedQuestions = questions.map((q) => {
        if (q.id !== selectedQuestionId) return q;

        if (field === "type" && value === "scale") {
          return {
            ...q,
            [field]: value,
            scale: "0-10",
            qualifier: ["Strongly Disagree", "Neutral", "Strongly Agree"],
            options: null,
          };
        } else if (
          field === "type" &&
          (value === "multi" || value === "single")
        ) {
          return {
            ...q,
            [field]: value,
            options: q.options || [""],
            scale: null,
            qualifier: null,
          };
        } else {
          return { ...q, [field]: value };
        }
      });

      updateFormData({
        questions: updatedQuestions.map(convertToExistingFormat),
      });
    },
    [selectedQuestionId, questions, updateFormData]
  );

  // Handle option changes for multi/single choice questions
  const handleOptionChange = useCallback(
    (optionIndex, value) => {
      if (!selectedQuestion) return;

      const newOptions = [...(selectedQuestion.options || [])];
      newOptions[optionIndex] = value;

      const updatedQuestions = questions.map((q) =>
        q.id === selectedQuestionId ? { ...q, options: newOptions } : q
      );

      updateFormData({
        questions: updatedQuestions.map(convertToExistingFormat),
      });
    },
    [selectedQuestionId, selectedQuestion, questions, updateFormData]
  );

  const handleAddOption = useCallback(() => {
    if (!selectedQuestion) return;

    const newOptions = [...(selectedQuestion.options || []), ""];
    const updatedQuestions = questions.map((q) =>
      q.id === selectedQuestionId ? { ...q, options: newOptions } : q
    );

    updateFormData({
      questions: updatedQuestions.map(convertToExistingFormat),
    });
  }, [selectedQuestionId, selectedQuestion, questions, updateFormData]);

  const handleRemoveOption = useCallback(
    (optionIndex) => {
      if (!selectedQuestion || !selectedQuestion.options) return;

      const newOptions = selectedQuestion.options.filter(
        (_, index) => index !== optionIndex
      );
      const updatedQuestions = questions.map((q) =>
        q.id === selectedQuestionId ? { ...q, options: newOptions } : q
      );

      updateFormData({
        questions: updatedQuestions.map(convertToExistingFormat),
      });
    },
    [selectedQuestionId, selectedQuestion, questions, updateFormData]
  );

  // Handle qualifier changes for scale questions
  const handleQualifierChange = useCallback(
    (position, value) => {
      if (!selectedQuestion) return;

      const newQualifiers = [...(selectedQuestion.qualifier || [])];
      newQualifiers[position] = value;

      const updatedQuestions = questions.map((q) =>
        q.id === selectedQuestionId ? { ...q, qualifier: newQualifiers } : q
      );

      updateFormData({
        questions: updatedQuestions.map(convertToExistingFormat),
      });
    },
    [selectedQuestionId, selectedQuestion, questions, updateFormData]
  );

  // Remove a question
  const handleRemoveQuestion = useCallback(
    (questionId) => {
      const updatedQuestions = questions.filter((q) => q.id !== questionId);
      updateFormData({
        questions: updatedQuestions.map(convertToExistingFormat),
      });
      if (selectedQuestionId === questionId) {
        setSelectedQuestionId(null);
      }
    },
    [selectedQuestionId, questions, updateFormData]
  );

  // Drag and drop handlers
  const handleDragStart = useCallback((questionId) => {
    setDraggedQuestionId(questionId);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e, targetQuestionId) => {
      e.preventDefault();

      if (!draggedQuestionId || draggedQuestionId === targetQuestionId) {
        setDraggedQuestionId(null);
        return;
      }

      const draggedIndex = questions.findIndex(
        (q) => q.id === draggedQuestionId
      );
      const targetIndex = questions.findIndex((q) => q.id === targetQuestionId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedQuestionId(null);
        return;
      }

      const newQuestions = [...questions];
      const [draggedQuestion] = newQuestions.splice(draggedIndex, 1);
      newQuestions.splice(targetIndex, 0, draggedQuestion);

      updateFormData({
        questions: newQuestions.map(convertToExistingFormat),
      });
      setDraggedQuestionId(null);
    },
    [draggedQuestionId, questions, updateFormData]
  );

  const getQuestionTypeLabel = (type) => {
    const questionType = QUESTION_TYPES.find((qt) => qt.value === type);
    return questionType ? questionType.label : type;
  };

  // Handle settings changes
  const handleSettingsChange = useCallback(
    (field, value) => {
      setSurveySettings((prev) => ({ ...prev, [field]: value }));

      // Update form data immediately
      if (field === "title") {
        updateFormData({ name: value });
      } else if (field === "description") {
        updateFormData({ description: value });
      } else if (field === "anonymous") {
        const existing = Array.isArray(formData?.user_tasks) ? formData.user_tasks : [];
        const idx = existing.findIndex(
          (t) => t && t.task_type === "survey_is_anonymous"
        );
        let updatedUserTasks;
        if (idx >= 0) {
          // update in place
          updatedUserTasks = existing.map((t, i) => (i === idx ? { ...t, value } : t));
        } else {
          // append task, preserve existing tasks
          updatedUserTasks = [...existing, { task_type: "survey_is_anonymous", value }];
        }
        updateFormData({ user_tasks: updatedUserTasks });
      }
    },
    [updateFormData, formData]
  );

  // Handle save - use existing context save functionality
  const handleSave = useCallback(() => {
    if (saveDraft) {
      saveDraft();
    }
  }, [saveDraft]);


  // Detect if this is a draft (survey with empty assigned_employees)
  const isDraftSurvey = useMemo(() => {
    // Check if this survey is a draft by looking for empty assigned_employees or draft flag
    if (!formData) return false;
    
    // Check for draft flag in user_tasks
    const isDraftFromLibrary = formData.draftData;

    
    // Check if this survey has no assigned employees (indicating it's a draft)
    const hasNoAssignedEmployees = !formData.assignedUsersList || formData.assignedUsersList.length === 0;
    
    return isDraftFromLibrary || (isEditMode && hasNoAssignedEmployees);
  }, [formData, isEditMode]);

  // Handle send survey - navigate to schedule page with existing draft data
  const handleSendSurvey = useCallback(() => {
    // Validate survey first and show errors if any
    if (!validateSurveyAndShowErrors()) {
      return; // Stop if there are validation errors
    }

    if (isDraftSurvey && formData) {
      // Use edit URL pattern if we have a survey ID
      const returnTo = formData.id 
        ? `/leader/audit/schedule-v2/pulse/edit/${formData.id}`
        : location.pathname + (location.search || '');
        
      navigate("/leader/audit/schedule-v2/pulse/schedule", {
        state: {
          existingDraftData: {
            ...formData,
            emailSubject: `Survey: ${formData.name || ""}`,
            emailBody: `Hi,\\n\\nYou have been invited to participate in the survey "${
              formData.name || ""
            }". Please follow the link to complete the survey.\\n\\nThank you!`,
            assignedUsersList: [],
          },
          returnTo,
        },
      });
    }
  }, [isDraftSurvey, formData, navigate, location, validateSurveyAndShowErrors]);

  // Handle save and send for new surveys
  const handleSaveAndSend = useCallback(() => {
    // Validate survey first and show errors if any
    if (!validateSurveyAndShowErrors()) {
      return; // Stop if there are validation errors
    }

    if (!isEditMode && formData && saveDraft) {
      // Set a flag to track that we want to navigate to schedule after save
      setShouldNavigateToScheduleAfterSave(true);
      // Save the survey as a draft
      saveDraft(false); // false = create as draft, not template
    }
  }, [isEditMode, formData, saveDraft, validateSurveyAndShowErrors]);



  // Handle share functionality
  const handleAddSharedUser = useCallback(
    (userId, role) => {
      // Find user by value in userList
      const user = userList.find((u) => u.value === userId);
      if (user && !sharedUsers.find((su) => su.id === userId)) {
        // We'll need a name and email for display; fallback if not available
        const newUser = {
          id: user.value,
          name: user.text,
          email: user.email || "", // user.email may not exist on userList entry
          role,
        };
        setSharedUsers((prev) => [...prev, newUser]);

        // Add to user_tasks array instead of separate field
        const newTask = {
          task_type: role,
          user_id: user.value,
          name: user.text,
          email: user.email || "",
        };

        const updatedUserTasks = [...(formData.user_tasks || []), newTask];
        updateFormData({ user_tasks: updatedUserTasks });
      }
    },
    [userList, sharedUsers, updateFormData, formData.user_tasks]
  );

  const handleRemoveSharedUser = useCallback(
    (userId) => {
      const updatedSharedUsers = sharedUsers.filter((u) => u.id !== userId);
      setSharedUsers(updatedSharedUsers);

      // Remove from user_tasks array
      const updatedUserTasks =
        formData.user_tasks?.filter(
          (task) =>
            !(
              (task && (task.task_type === "collaborator" ||
                task.task_type === "viewer")) &&
              task.user_id === userId
            )
        ) || [];
      updateFormData({ user_tasks: updatedUserTasks });
    },
    [sharedUsers, updateFormData, formData.user_tasks]
  );

  const handleChangeUserRole = useCallback(
    (userId, newRole) => {
      const updatedSharedUsers = sharedUsers.map((u) =>
        u.id === userId ? { ...u, role: newRole } : u
      );
      setSharedUsers(updatedSharedUsers);

      // Update user_tasks array - find and update the user's task
      const updatedUserTasks =
        formData.user_tasks?.map((task) => {
          if (
            (task && (task.task_type === "collaborator" ||
              task.task_type === "viewer")) &&
            task.user_id === userId
          ) {
            return { ...task, task_type: newRole };
          }
          return task;
        }) || [];

      updateFormData({ user_tasks: updatedUserTasks });
    },
    [sharedUsers, updateFormData, formData.user_tasks]
  );

  return (
    <Container data-mode={isEditMode ? "edit" : "create"}>
      {/* Header */}
      <Header>
        <HeaderContent>
          <HeaderLeft>
            <BackButton onClick={handleBackClick}>
              <ArrowLeftIcon />
              <span>Back</span>
            </BackButton>
            <TitleGroup>
              <TitleRow>
                <Title>Pulse Builder</Title>
                <ModeBadge data-mode={isEditMode ? "edit" : "create"}>
                  {isEditMode ? "Edit Mode" : "New Survey"}
                </ModeBadge>
              </TitleRow>
              <Subtitle>Design your survey questions</Subtitle>
              {!isEditMode && (
                <ProgressIndicator>
                  {surveyProgress.steps.map((step) => (
                    <ProgressStep key={step.key} completed={step.completed}>
                      <ProgressCheckmark completed={step.completed}>
                        {step.completed ? "✓" : "○"}
                      </ProgressCheckmark>
                      <ProgressLabel>{step.label}</ProgressLabel>
                    </ProgressStep>
                  ))}
                </ProgressIndicator>
              )}
            </TitleGroup>
          </HeaderLeft>
          <HeaderRight>
            {onSwitchToAI && canEdit && (
              <HeaderButton onClick={onSwitchToAI}>
                <SettingsIcon>🤖</SettingsIcon>
                AI Builder
              </HeaderButton>
            )}
            {canEdit && (
              <HeaderButton 
                onClick={() => setShowSettingsModal(true)}
                hasError={showValidationErrors && hasBasicInfoValidationIssues()}
              >
                <SettingsIcon>⚙️</SettingsIcon>
                Settings
                {showValidationErrors && hasBasicInfoValidationIssues() && <ErrorIndicator>!</ErrorIndicator>}
              </HeaderButton>
            )}
            {userRole === "creator" && (
              <HeaderButton onClick={() => setShowShareModal(true)}>
                <SettingsIcon>👥</SettingsIcon>
                Share
              </HeaderButton>
            )}
            {canEdit && (
              <>
                {(!isEditMode && !isDraftSurvey) ? (
                  <>
                    <SaveButton onClick={handleSave} disabled={loading}>
                      <SaveIcon>💾</SaveIcon>
                      {loading ? "Saving..." : "Save Template"}
                    </SaveButton>
                    <SendSurveyButton onClick={handleSaveAndSend} disabled={loading}>
                      <SendIcon>📤</SendIcon>
                      Save & Send
                    </SendSurveyButton>
                  </>
                ) : (
                  <>
                    <SaveButton onClick={handleSave} disabled={loading}>
                      <SaveIcon>💾</SaveIcon>
                      {loading ? "Saving..." : "Save Draft"}
                    </SaveButton>
                  </>
                )}
              </>
            )}
            {isDraftSurvey && (
              <SendSurveyButton onClick={handleSendSurvey} disabled={loading}>
                <SendIcon>📤</SendIcon>
                Send Survey
              </SendSurveyButton>
            )}
          </HeaderRight>
        </HeaderContent>
      </Header>

      {/* Main Content */}
      <MainContent>
        {/* Left Panel - Question List */}
        <LeftPanel>
          <PanelHeader>
            <PanelTitle>Content</PanelTitle>
            {canEdit && (
              <AddContentWrapper>
                <AddContentButton
                  onClick={() => setShowAddDropdown(!showAddDropdown)}
                  active={showAddDropdown}
                >
                  <AddIcon>+</AddIcon>
                  Add content
                </AddContentButton>

                {showAddDropdown && (
                  <AddContentDropdown>
                    <DropdownSection>
                      <DropdownSectionTitle>
                        Standard Questions
                      </DropdownSectionTitle>
                      {QUESTION_TYPES.map((type) => (
                        <DropdownItem
                          key={type.value}
                          onClick={() => handleAddQuestion(type.value)}
                        >
                          <DropdownItemIcon>{type.icon}</DropdownItemIcon>
                          <DropdownItemContent>
                            <DropdownItemTitle>{type.label}</DropdownItemTitle>
                            <DropdownItemDescription>
                              {type.description}
                            </DropdownItemDescription>
                          </DropdownItemContent>
                        </DropdownItem>
                      ))}
                    </DropdownSection>
                  </AddContentDropdown>
                )}
              </AddContentWrapper>
            )}
          </PanelHeader>

          <QuestionList>
            {questions.length === 0 ? (
              <EmptyState>
                <EmptyIcon>📝</EmptyIcon>
                <EmptyTitle>No content yet</EmptyTitle>
                <EmptyText>
                  {!surveyProgress.basicInfo ? 
                    "Complete your survey setup first, then add questions using the button above" :
                    "Add content using the button above"
                  }
                </EmptyText>
              </EmptyState>
            ) : (
              questions.map((question, index) => {
                const hasError = showValidationErrors && hasQuestionValidationIssues(question);
                return (
                  <QuestionListItem
                    key={question.id}
                    draggable
                    onDragStart={() => handleDragStart(question.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, question.id)}
                    selected={selectedQuestionId === question.id}
                    hasError={hasError}
                    onClick={() => setSelectedQuestionId(question.id)}
                  >
                    <QuestionListItemContent>
                      <QuestionNumber hasError={hasError}>{index + 1}</QuestionNumber>
                      <QuestionListItemText>
                        <QuestionListItemTitle hasError={hasError}>
                          {question.question || "Untitled"}
                          {hasError && <ErrorIndicator>!</ErrorIndicator>}
                        </QuestionListItemTitle>
                        <QuestionListItemType>
                          {getQuestionTypeLabel(question.type)}
                        </QuestionListItemType>
                      </QuestionListItemText>
                      <DragHandle>⋮⋮</DragHandle>
                    </QuestionListItemContent>
                  </QuestionListItem>
                );
              })
            )}
          </QuestionList>
        </LeftPanel>

        {/* Center Panel - Question Editor */}
        <CenterPanel>
          {selectedQuestion ? (
            <QuestionEditor>
              <QuestionEditorHeader>
                <QuestionEditorTitle>
                  Question #
                  {questions.findIndex((q) => q.id === selectedQuestion.id) + 1}
                </QuestionEditorTitle>
                <RemoveQuestionButton
                  onClick={() => handleRemoveQuestion(selectedQuestion.id)}
                >
                  <DeleteIcon>🗑️</DeleteIcon>
                </RemoveQuestionButton>
              </QuestionEditorHeader>

              <QuestionEditorContent>
                {/* Question Title */}
                <FormGroup>
                  <Label>Your question title here</Label>
                  <Input
                    type="text"
                    value={selectedQuestion.question}
                    onChange={
                      canEdit
                        ? (e) =>
                            handleQuestionUpdate("question", e.target.value)
                        : undefined
                    }
                    placeholder="Enter your question"
                    readOnly={!canEdit}
                  />
                  <HelpText>(Add an optional description)</HelpText>
                </FormGroup>

                {/* Question Type */}
                <FormGroup>
                  <Label>Question Type</Label>
                  <Select
                    value={selectedQuestion.type}
                    onChange={(e) =>
                      handleQuestionUpdate("type", e.target.value)
                    }
                  >
                    {QUESTION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                {/* Scale Options */}
                {selectedQuestion.type === "scale" && (
                  <>
                    <FormGroup>
                      <Label>Scale</Label>
                      <Select
                        value={selectedQuestion.scale}
                        onChange={(e) =>
                          handleQuestionUpdate("scale", e.target.value)
                        }
                      >
                        <option value="0-10">10 Point</option>
                        <option value="0-5">5 Point</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Qualifiers</Label>
                      <QualifierInputs>
                        <QualifierGroup>
                          <QualifierLabel>Minimum</QualifierLabel>
                          <Input
                            type="text"
                            value={selectedQuestion.qualifier?.[0] || ""}
                            onChange={(e) =>
                              handleQualifierChange(0, e.target.value)
                            }
                            placeholder="Strongly Disagree"
                          />
                        </QualifierGroup>
                        <QualifierGroup>
                          <QualifierLabel>Middle</QualifierLabel>
                          <Input
                            type="text"
                            value={selectedQuestion.qualifier?.[1] || ""}
                            onChange={(e) =>
                              handleQualifierChange(1, e.target.value)
                            }
                            placeholder="Neutral"
                          />
                        </QualifierGroup>
                        <QualifierGroup>
                          <QualifierLabel>End</QualifierLabel>
                          <Input
                            type="text"
                            value={selectedQuestion.qualifier?.[2] || ""}
                            onChange={(e) =>
                              handleQualifierChange(2, e.target.value)
                            }
                            placeholder="Strongly Agree"
                          />
                        </QualifierGroup>
                      </QualifierInputs>
                    </FormGroup>
                  </>
                )}

                {/* Multiple Choice Options */}
                {(selectedQuestion.type === "multi" ||
                  selectedQuestion.type === "single") && (
                  <FormGroup>
                    <Label>Options</Label>
                    <OptionsContainer>
                      {(selectedQuestion.options || []).map((option, index) => (
                        <OptionRow key={index}>
                          <Input
                            type="text"
                            value={option}
                            onChange={(e) =>
                              handleOptionChange(index, e.target.value)
                            }
                            placeholder={`Option ${index + 1}`}
                          />
                          {selectedQuestion.options &&
                            selectedQuestion.options.length > 1 && (
                              <RemoveOptionButton
                                onClick={() => handleRemoveOption(index)}
                              >
                                ×
                              </RemoveOptionButton>
                            )}
                        </OptionRow>
                      ))}
                      <AddOptionButton onClick={handleAddOption}>
                        + Add option
                      </AddOptionButton>
                    </OptionsContainer>
                  </FormGroup>
                )}

                {/* AI Enhanced Toggle */}
                <FormGroup>
                  <ToggleRow>
                    <ToggleLabel>AI Enhanced</ToggleLabel>
                    <Toggle
                      type="checkbox"
                      checked={selectedQuestion.aiEnhanced}
                      onChange={(e) =>
                        handleQuestionUpdate("aiEnhanced", e.target.checked)
                      }
                    />
                  </ToggleRow>
                  <HelpText>
                    Use an AI-designed follow-up question to encourage
                    elaboration
                  </HelpText>
                </FormGroup>
              </QuestionEditorContent>
            </QuestionEditor>
          ) : (
            <NoQuestionSelected>
              <NoQuestionIcon>📝</NoQuestionIcon>
              <NoQuestionTitle>No content selected</NoQuestionTitle>
              <NoQuestionText>
                Select a content item from the list on the left to preview and
                edit it here.
              </NoQuestionText>
            </NoQuestionSelected>
          )}
        </CenterPanel>
      </MainContent>

      {/* Settings Modal */}
      {showSettingsModal && (
        <ModalOverlay onClick={() => setShowSettingsModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {!isEditMode && !surveyProgress.basicInfo ? "Survey Setup" : "Survey Settings"}
              </ModalTitle>
              <CloseButton onClick={() => setShowSettingsModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              {!isEditMode && !surveyProgress.basicInfo && (
                <SetupPrompt>
                  <SetupPromptIcon>📝</SetupPromptIcon>
                  <SetupPromptText>
                    Let's start by setting up the basic information for your survey. 
                    This will help respondents understand what the survey is about.
                  </SetupPromptText>
                </SetupPrompt>
              )}
              <FormGroup>
                <Label>Survey Title</Label>
                <Input
                  type="text"
                  value={surveySettings.title}
                  onChange={(e) =>
                    handleSettingsChange("title", e.target.value)
                  }
                  placeholder="Enter survey title"
                />
              </FormGroup>

              <FormGroup>
                <Label>Survey Description</Label>
                <TextArea
                  value={surveySettings.description}
                  onChange={(e) =>
                    handleSettingsChange("description", e.target.value)
                  }
                  placeholder="Enter survey description"
                  rows={4}
                />
              </FormGroup>

              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Make this survey anonymous</ToggleLabel>
                  <Toggle
                    type="checkbox"
                    checked={surveySettings.anonymous}
                    onChange={(e) =>
                      handleSettingsChange("anonymous", e.target.checked)
                    }
                  />
                </ToggleRow>
                <HelpText>
                  Anonymous surveys don't collect respondent identities
                </HelpText>
              </FormGroup>
            </ModalContent>

            <ModalFooter>
              <ModalCancelButton onClick={() => setShowSettingsModal(false)}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton 
                onClick={() => setShowSettingsModal(false)}
                disabled={!surveySettings.title.trim() || !surveySettings.description.trim()}
              >
                {!isEditMode && !surveyProgress.basicInfo ? "Get Started" : "Save Settings"}
              </ModalSaveButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ModalOverlay onClick={() => setShowShareModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Share Survey</ModalTitle>
              <CloseButton onClick={() => setShowShareModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              <FormGroup>
                <Label>Add Collaborators or Viewers</Label>
                <ShareSection>
                  <ShareDropdownContainer>
                    <PulseBuilderStyledSearchDropdown
                      style={{theme: "pulsebuilder"}}
                      data={userList.filter(
                        (user) =>
                          !sharedUsers.find((su) => su.id === user.value)
                      )}
                      onSearch={(item, term) =>
                        item.text.toLowerCase().includes(term.toLowerCase())
                      }
                      placeholder="Search users to add"
                      onSelect={(item) => {
                        // Default new users to Viewer
                        handleAddSharedUser(item.value, "viewer");
                      }}
                      maxScrollHeight="150px"
                    />
                  </ShareDropdownContainer>

                  <SharedUsersList>
                    {sharedUsers.map((user) => (
                      <SharedUserItem key={user.id}>
                        <SharedUserInfo>
                          <SharedUserName>{user.name}</SharedUserName>
                          <SharedUserEmail>{user.email}</SharedUserEmail>
                        </SharedUserInfo>
                        <UserRoleSection>
                          <Select
                            value={user.role}
                            onChange={(e) =>
                              handleChangeUserRole(user.id, e.target.value)
                            }
                            style={{ marginRight: "8px", minWidth: "120px" }}
                          >
                            <option value="collaborator">Collaborator</option>
                            <option value="viewer">Viewer</option>
                          </Select>
                          <RemoveUserButton
                            onClick={() => handleRemoveSharedUser(user.id)}
                          >
                            ×
                          </RemoveUserButton>
                        </UserRoleSection>
                      </SharedUserItem>
                    ))}
                    {sharedUsers.length === 0 && (
                      <EmptySharedUsers>
                        No users have access yet
                      </EmptySharedUsers>
                    )}
                  </SharedUsersList>
                </ShareSection>
              </FormGroup>

              <FormGroup>
                <HelpText>
                  <strong>Collaborator:</strong> Can view, edit, and send
                  surveys
                  <br />
                  <strong>Viewer:</strong> Can view and send surveys, but cannot
                  edit
                </HelpText>
              </FormGroup>
            </ModalContent>

            <ModalFooter>
              <ModalCancelButton onClick={() => setShowShareModal(false)}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton onClick={() => setShowShareModal(false)}>
                Save Sharing Settings
              </ModalSaveButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

const PulseBuilderWithUserSearch = (props) => (
  <UserSearchProvider>
    <PulseBuilderV2Integrated {...props} />
  </UserSearchProvider>
);
export default PulseBuilderWithUserSearch;

// Styled Components (reusing from original PulseBuilderV2)
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  font-family: "Poppins", sans-serif;
`;

const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${props => props.hasError ? '#fef2f2' : 'transparent'};
  border: 1px solid ${props => props.hasError ? '#fecaca' : '#e0e0e0'};
  border-radius: 6px;
  color: ${props => props.hasError ? '#dc3545' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.hasError ? '#fef2f2' : '#f8f9fa'};
    border-color: ${props => props.hasError ? '#f87171' : '#c0c0c0'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #476dfa;
  }
`;


const SettingsIcon = styled.span`
  font-size: 14px;
`;

const SaveIcon = styled.span`
  font-size: 14px;
`;

const SendSurveyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #28a745;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #218838;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #28a745;
  }
`;

const SendIcon = styled.span`
  font-size: 14px;
`;


const Title = styled.h1`
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  border: 1px solid #e0e0e0;
  color: #555;
  background: ${(p) => (p["data-mode"] === "edit" ? "#fff3cd" : "#e7f5ff")};
  border-color: ${(p) => (p["data-mode"] === "edit" ? "#ffe08a" : "#a5d8ff")};
`;

const MainContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 300px 1fr;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
`;

const LeftPanel = styled.div`
  background: white;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
`;

const CenterPanel = styled.div`
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  padding: 24px;
  min-height: calc(100vh - 80px);
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const PanelTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const AddContentWrapper = styled.div`
  position: relative;
  margin-top: 12px;
`;

const AddContentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.active ? '#476dfa' : '#1a202c'};
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#3d5ce6' : '#2d3748'};
  }
`;

const AddIcon = styled.span`
  font-weight: 600;
  font-size: 16px;
`;

const AddContentDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
`;

const DropdownSection = styled.div`
  padding: 8px 0;
`;

const DropdownSectionTitle = styled.div`
  padding: 8px 16px;
  font-weight: 600;
  font-size: 12px;
  color: #7e7e7e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const DropdownItemIcon = styled.span`
  font-size: 16px;
  width: 20px;
  text-align: center;
`;

const DropdownItemContent = styled.div`
  flex: 1;
`;

const DropdownItemTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 2px;
`;

const DropdownItemDescription = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const QuestionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const QuestionListItem = styled.div`
  background: ${props => {
    if (props.hasError) return '#fef2f2';
    if (props.selected) return '#f0f4ff';
    return 'white';
  }};
  border: 1px solid ${props => {
    if (props.hasError) return '#fecaca';
    if (props.selected) return '#476dfa';
    return '#e0e0e0';
  }};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.hasError ? '#f87171' : '#476dfa'};
    background: ${props => props.hasError ? '#fef2f2' : '#f0f4ff'};
  }
`;

const QuestionListItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
`;

const QuestionNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${props => props.hasError ? '#fecaca' : '#f8f9fa'};
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.hasError ? '#dc3545' : '#7e7e7e'};
  flex-shrink: 0;
`;

const QuestionListItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuestionListItemTitle = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: ${props => props.hasError ? '#dc3545' : '#414141'};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const QuestionListItemType = styled.div`
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const ErrorIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
`;

const DragHandle = styled.div`
  color: #c0c0c0;
  cursor: grab;
  font-size: 12px;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptyText = styled.p`
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const QuestionEditor = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const QuestionEditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const QuestionEditorTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const RemoveQuestionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const DeleteIcon = styled.span`
  font-size: 14px;
`;

const QuestionEditorContent = styled.div`
  padding: 20px;
  flex: 1;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #7e7e7e;
  margin-top: 4px;
  font-style: italic;
`;

const QualifierInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`;

const QualifierGroup = styled.div``;

const QualifierLabel = styled.label`
  display: block;
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 6px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveOptionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #dc3545;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const AddOptionButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  border: 1px dashed #c0c0c0;
  border-radius: 6px;
  color: #476dfa;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    background: #f0f4ff;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const Toggle = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const NoQuestionSelected = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 48px 24px;
`;

const NoQuestionIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const NoQuestionTitle = styled.h3`
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const NoQuestionText = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 20px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const ModalContent = styled.div`
  padding: 20px 24px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const ModalCancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const ModalSaveButton = styled.button`
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #476dfa;

    &:hover {
      background: #476dfa;
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

// Share Components
const ShareSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShareDropdownContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1001;
`;

const SharedUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const SharedUserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
`;

const SharedUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SharedUserName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
`;

const SharedUserEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const UserRoleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveUserButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const EmptySharedUsers = styled.div`
  padding: 20px;
  text-align: center;
  color: #7e7e7e;
  font-family: "Poppins";
  font-size: 13px;
  font-style: italic;
`;

// Progress Indicator Components
const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 12px;
  padding: 8px 0;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProgressCheckmark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  color: ${props => props.completed ? '#ffffff' : '#c0c0c0'};
  background: ${props => props.completed ? '#28a745' : 'transparent'};
  border: 2px solid ${props => props.completed ? '#28a745' : '#c0c0c0'};
  transition: all 0.2s ease;
`;

const ProgressLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #7e7e7e;
`;

const CompletionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  color: white;
  background: #28a745;
  margin-left: 4px;
`;

// Setup Prompt Components
const SetupPrompt = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f0f4ff;
  border: 1px solid #a5d8ff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SetupPromptIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const SetupPromptText = styled.p`
  font-family: "Poppins";
  font-size: 14px;
  color: #414141;
  margin: 0;
  line-height: 1.5;
`;
